<template>
  <!-- left button (decrement) -->
  <div>
    <div class="d-flex align-items-center">
      <button
        type="button"
        class="btn btn-lg btn-warning float-right"
        @click="decrement"
        :disabled="disabled || isMin"
      >
        -
      </button>
      <div class="col form-group ms-2 me-2">
        <div class="form-floating">
          <input
            class="form-control"
            style="text-align: center"
            v-model="return_count"
            :class="[
              $attrs.class,
              { 'is-invalid': error, 'bb-warning': isMax },
            ]"
            v-bind="$attrs"
            autocomplete="off"
            @keydown="onKeyDown($event)"
            @keyup="onKeyUp($event)"
            :id="pass_ref"
            :ref="pass_ref"
            :maxlength="pass_digits + (pass_min < 0 ? 1 : 0)"
            :disabled="disabled"
          />
          <label
            v-if="pass_optional"
            class="optional bb-float-label"
            :class="{ 'bb-warning': isMax }"
            for="pass_ref"
            >{{ this.pass_label }}</label
          >
          <label
            v-else
            class="bb-float-label"
            :class="{ 'bb-warning': isMax }"
            for="pass_ref"
            >{{ this.pass_label }}</label
          >
        </div>
      </div>
      <button
        type="button"
        class="btn btn-lg btn-primary float-left"
        @click="increment"
        :disabled="disabled || isMax"
      >
        +
      </button>
    </div>
    <!-- ------------ -->
    <!--    errors    -->
    <!-- ------------ -->
    <div
      v-if="error"
      class="row mt-3 bb-error justify-content-center"
    >
      {{ error }}
    </div>
  </div>
</template>

<script>
//----------------------
// scripts
//----------------------
export default {
  name: 'CounterComponent',
  // allow forwarding parent attrs (classes) to the inner input
  inheritAttrs: false,
  //----------------------
  // props
  //----------------------
  props: {
    pass_label: {
      type: String,
      required: false,
    },
    pass_count: {
      type: Number,
      required: true,
    },
    pass_min: {
      type: Number,
      required: true,
    },
    pass_max: {
      type: Number,
      required: true,
    },
    pass_ref: {
      type: String,
      required: true,
    },
    pass_digits: {
      type: Number,
      required: true,
    },
    pass_optional: {
      type: Boolean,
      required: false,
      default: false,
    },
    pass_skip_zero: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  //----------------------
  // data
  //----------------------
  data() {
    return {
      error: '',
      return_count: null,
    }
  },
  //----------------------
  // computed
  //----------------------
  computed: {
    currentValue() {
      const value = this.return_count ?? this.pass_count
      const numericValue = Number(value)
      return Number.isNaN(numericValue) ? null : numericValue
    },
    isMin() {
      return this.currentValue !== null && this.currentValue <= this.pass_min
    },
    isMax() {
      return this.currentValue !== null && this.currentValue >= this.pass_max
    },
  },
  //----------------------
  // methods
  //----------------------
  methods: {
    //----------------------------
    // increment
    //----------------------------
    increment() {
      // start from current displayed value, falling back to prop if needed
      let next = this.return_count ?? this.pass_count
      next = next + 1
      if (next > this.pass_max) next = this.pass_min
      if (this.pass_skip_zero && next == 0) next = 1
      this.return_count = next
      this.emitter.emit(this.pass_ref, this.return_count)
    },
    //----------------------------
    // decrement
    //----------------------------
    decrement() {
      // start from current displayed value, falling back to prop if needed
      let next = this.return_count ?? this.pass_count
      next = next - 1
      if (next < this.pass_min) next = this.pass_max
      if (this.pass_skip_zero && next == 0) {
        next = -1
        if (next < this.pass_min) return
      }
      this.return_count = next
      this.emitter.emit(this.pass_ref, this.return_count)
    },
    //----------------------------
    // clear
    //----------------------------
    clear(e) {
      this.error = ''
      this.return_count = 0
      e.target.value = ''
    },
    //----------------------------
    // onKeyDown
    //----------------------------
    onKeyDown(e) {
      // allow navigation keys
      const navKeys = [
        'Backspace',
        'Tab',
        'ArrowLeft',
        'ArrowRight',
        'Delete',
        'Home',
        'End',
      ]
      if (navKeys.includes(e.key)) return

      // allow minus if negative values permitted and not already present
      if (e.key === '-') {
        if (this.pass_min < 0 && !e.target.value.includes('-')) return
        e.preventDefault()
        return
      }

      // allow digits only otherwise
      if (!/^[0-9]$/.test(e.key)) {
        e.preventDefault()
        return
      }

      // if single-digit mode, ensure the digit is within min/max
      if (this.pass_digits == 1) {
        if (!this.isSingleDigitRange(e, this.pass_min, this.pass_max)) {
          e.preventDefault()
        }
      }
    },
    //----------------------------
    // onKeyUp
    //----------------------------
    onKeyUp(e) {
      const val = e.target.value

      // allow partial leading '-' (user still typing) when negatives allowed
      if (val === '-' && this.pass_min < 0) {
        this.error = ''
        this.return_count = val // keep as string until a full number typed
        return
      }

      // empty input => clear
      if (val === '') {
        this.error = ''
        this.return_count = null
        // emit null so parent can react if needed
        this.emitter.emit(this.pass_ref, null)
        return
      }

      const num = Number(val)
      if (isNaN(num)) {
        // invalid non-numeric input
        this.error = 'Invalid number'
        return
      }

      if (this.pass_digits == 2) {
        if (!this.isRange(e, this.pass_min, this.pass_max)) {
          this.error =
            'Invalid ' +
            this.pass_label +
            ' (Valid range: ' +
            this.pass_min +
            '-' +
            this.pass_max +
            ')'
          e.preventDefault()
        } else {
          this.error = ''
          this.return_count = num
          this.emitter.emit(this.pass_ref, this.return_count)
        }
      } else {
        // non-range mode: enforce min/max and emit when valid
        if (num < this.pass_min || num > this.pass_max) {
          this.error =
            'Invalid ' +
            this.pass_label +
            ' (Valid range: ' +
            this.pass_min +
            '-' +
            this.pass_max +
            ')'
        } else {
          this.error = ''
          this.return_count = num
          this.emitter.emit(this.pass_ref, this.return_count)
        }
      }
    },
    //----------------------------
    // isNumber
    //----------------------------
    isNumber(e) {
      // accept digits and minus when allowed
      const k = e.key || String.fromCharCode(e.keyCode)
      if (k === '-') return this.pass_min < 0 && !e.target.value.includes('-')
      return /^[0-9]$/.test(k)
    },
    //----------------------------
    // isSingleDigitRange
    //----------------------------
    isSingleDigitRange(e, min, max) {
      const k = e.key
      if (!/^[0-9]$/.test(k)) return false
      const num = Number(k)
      return num >= min && num <= max
    },
    //----------------------------
    // isRange
    //----------------------------
    isRange(e, min, max) {
      const value = e.target.value

      // allow leading '-' as partial input when negative min allowed
      if (value === '-' && min < 0) return false
      if (value === '') return false
      const num = Number(value)
      if (isNaN(num)) return false

      if (num < min || num > max) {
        return false
      } else {
        return true
      }
    },
  },
  //----------------------------
  // watch
  //----------------------------
  watch: {
    pass_count() {
      this.return_count = this.pass_count
    },
  },
  //----------------------
  // mounted
  //----------------------
  mounted() {
    this.return_count = this.pass_count
  },
}
</script>
